import { isNil, uniq } from 'lodash'
import React, { useMemo } from 'react'
import i18n from 'simple-react-i18n'
import { shallowEqual, useSelector } from 'react-redux'
import { getRemarks } from 'utils/AnalyseUtils'
import PropTypes from 'prop-types'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import { Grid } from '@mui/material'
import { getBoundedThresholdLabel, getDecreasingThresholdLabel, getIncreasingThresholdLabel, THRESHOLD_COLOR, THRESHOLD_COLOR_BY_SIZE, THRESHOLD_TYPE } from 'utils/QualityThresholdUtils'

const RemarkLegend = ({
    list = [],
    col = 3,
    iconColor,
}) => {
    const {
        remarks,
    } = useSelector(store => ({
        remarks: store.OperationReducer.remarks,
    }), shallowEqual)
    const remarksUsed = uniq(list.map(e => e.remark))
    const remarkList = remarksUsed.map(remark => remarks.find(r => r.code === `${remark}`)).filter(r => !!r)
    return (
        <StyledFieldSet /* style={{ height: '100%' }}*/>
            <StyledLegend>{i18n.remarkCodes}</StyledLegend>
            <Grid container justifyContent='left' columnSpacing={2}>
                {
                    remarkList.map(({ code, name }, i) => {
                        const remark = getRemarks(code)
                        return !!remark && (
                            <Grid size={col} key={`name-${i}`} >
                                <span className={iconColor ? undefined : 'icon-color-blue'} style={{ paddingRight: 5, color: iconColor }}>{remark.logo}</span>
                                <span>{name}</span>
                            </Grid>
                        )
                    })
                }
            </Grid>
        </StyledFieldSet>
    )
}

RemarkLegend.propTypes = {
    list: PropTypes.arrayOf(PropTypes.shape({
        remark: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })),
    col: PropTypes.number,
    iconColor: PropTypes.string,
}

const ThresholdLegend = ({
    threshold = {},
    col = 6,
}) => {
    const legends = useMemo(() => {
        if (isNil(threshold)) return []

        if (threshold.thresholdType === THRESHOLD_TYPE.BOUNDED) {
            return [
                { label: getBoundedThresholdLabel(0, threshold), color: THRESHOLD_COLOR.red },
                { label: getBoundedThresholdLabel(1, threshold), color: THRESHOLD_COLOR.blue },
                { label: getBoundedThresholdLabel(2, threshold), color: THRESHOLD_COLOR.red },
            ]
        }

        // js cheat, adding boolean give a number: true = 1 and false = 0
        const nbThreshold = 1 + !isNil(threshold.threshold1) + !isNil(threshold.threshold2) + !isNil(threshold.threshold3) + !isNil(threshold.threshold4)

        if (threshold.thresholdType === THRESHOLD_TYPE.INCREASING) {
            return [
                ...THRESHOLD_COLOR_BY_SIZE[nbThreshold].map((color, i) => ({
                    color,
                    label: getIncreasingThresholdLabel(i, threshold),
                })),
                {
                    color: 'black',
                    label: i18n.thresholdNotApplicable,
                },
            ]
        }

        return [
            ...THRESHOLD_COLOR_BY_SIZE[nbThreshold].map((color, i) => ({
                color,
                label: getDecreasingThresholdLabel(i, threshold),
            })),
            {
                color: 'black',
                label: i18n.thresholdNotApplicable,
            },
        ]
    }, [threshold])

    return (
        <StyledFieldSet /* style={{ height: '100%' }}*/>
            <StyledLegend>{i18n.thresholds}</StyledLegend>
            <Grid container justifyContent='left' columnSpacing={2}>
                {
                    legends.map(({ color, label }, i) => {
                        return (
                            <Grid container size={col} key={`name-${i}`} columnSpacing={1} alignItems='center'>
                                <Grid>
                                    <div style={{ width: 20, height: 15, backgroundColor: color, borderRadius: '5px' }} />
                                </Grid>
                                <Grid>
                                    <span>{label}</span>
                                </Grid>
                            </Grid>
                        )
                    })
                }
            </Grid>
        </StyledFieldSet>
    )
}

ThresholdLegend.propTypes = {
    threshold: PropTypes.shape({
        threshold1: PropTypes.number,
        threshold2: PropTypes.number,
        threshold3: PropTypes.number,
        threshold4: PropTypes.number,
    }),
    col: PropTypes.number,
}

export {
    RemarkLegend,
    ThresholdLegend,
}